import { FOLDER_ROOT_PATH, parseFolderPath } from '@lib/library/folderPath';
import { parseModalTab } from '@lib/media/modalTab';
import { extractObjectId, parseMediaTime } from '@lib/utils/url';
import { LibraryPage } from '@pages/Library/LibraryPage';
import { useUIPreferences } from '@stores/UIPreferences';
import { observer } from 'mobx-react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';

export const LibraryPageWrapper = observer(function LibraryPageWrapper() {
  const { id: rawId, mediaId: rawMediaId } = useParams();
  const [searchParams] = useSearchParams();
  const uiPreferences = useUIPreferences();

  if (!rawId) {
    return <Navigate to="/libraries" replace />;
  }

  const id = extractObjectId(rawId);
  if (!id) {
    // TODO: Show an error page instead of silently redirecting when the library ID is invalid
    return <Navigate to="/libraries" replace />;
  }

  const mediaId = rawMediaId ? extractObjectId(rawMediaId) : undefined;
  // If mediaId param exists but is invalid, redirect to the library without the media
  // TODO: Show an error page instead of silently redirecting when the media ID is invalid
  if (rawMediaId && !mediaId) {
    return <Navigate to={`/library/${rawId}`} replace />;
  }

  // Use the saved preference when the URL names no tab, or names one we don't have. This is
  // the only place a `?tab=` is read, and it hands a `ModalTab` down from here — nothing below
  // re-checks it, which is exactly how the Scenes tab got dropped on the way to the modal.
  const tab = parseModalTab(searchParams.get('tab')) ?? uiPreferences.momentModalDefaultTab;

  // `?t=90` — where to put the playhead, YouTube-style. `parseMediaTime` returns null for
  // anything it can't read, and null must stay null all the way down: a garbled or absent `t`
  // means "open it the way you normally would", which is not the same as "open it at 0".
  // Deliberately not clamped to the duration here — this component has no media object to
  // clamp against, and the player clamps to its own clip bounds anyway.
  const startAt = parseMediaTime(searchParams.get('t')) ?? undefined;

  // `?comment=` — from a comment's "Copy link". An id the API never issued is dropped here, like
  // a garbled `t`: the link still opens the media at its moment, it just has nothing to select.
  const rawCommentId = searchParams.get('comment');
  const commentId = (rawCommentId && extractObjectId(rawCommentId)) || undefined;

  // `?folder=/Trips/Lisbon` — the folder the library opens in. Read on the library route only: the
  // media route overlays the grid, which keeps showing the folder it had. A spelling no folder
  // could have opens the root; one naming a folder the library lacks is resolved by the page,
  // which knows the tree.
  const folderPath = rawMediaId
    ? undefined
    : (parseFolderPath(searchParams.get('folder') ?? '') ?? FOLDER_ROOT_PATH);

  return (
    <LibraryPage
      id={id}
      mediaId={mediaId || undefined}
      tab={tab}
      startAt={startAt}
      commentId={commentId}
      folderPath={folderPath}
    />
  );
});
